import type { OverviewChartRow } from "@nexus/contracts";

import {
  computeMetricDomains,
  domainPercent,
  rawMetricKey,
  type ChartDatum,
  type ResolvedMetric,
} from "../components/OverviewChart/chartModel.js";
import { formatTopicTitle } from "../components/OverviewChart/chartPrimitives.js";

export const CHART_CSV_MIME = "text/csv;charset=utf-8";

/** Header of the first column — matches the Table view's Concept column. */
const CONCEPT_COLUMN = "Concept";

/**
 * Name the dimensions a Concept belongs to — the Concept label alone can repeat across
 * them. Fixed English, like the Competitors file: client reporting parses columns by name.
 */
const SCENARIO_COLUMN = "Scenario";
const SUB_CATEGORY_COLUMN = "Sub-category";
const SECTION_COLUMN = "Section";
const FORMAT_COLUMN = "Format";

/** Joins a topic's values in one cell, matching the Competitors export's list cells. */
const LIST_SEPARATOR = ",";

/** Retool renders the Section pair as "Hook & Post-Hook". */
const SECTION_SEPARATOR = " & ";

/** Suffix marking the normalized column that sits beside each metric's raw value. */
const PERCENT_COLUMN_SUFFIX = "(% of range)";

/** Decimal places kept on the normalized percent, mirroring the chart's own precision. */
const PERCENT_DECIMALS = 1;

/**
 * Quote a field only when it would otherwise break the row, and collapse embedded
 * newlines — matching the Retool `downloadOVChartData` export this replaces, so a
 * file opened side-by-side with an old one parses identically.
 */
function escapeCsvField(value: string): string {
  const clean = value.replace(/[\n\r]/g, " ").trim();
  if (!clean.includes('"') && !clean.includes(",") && !clean.includes("\n")) return clean;
  return `"${clean.replace(/"/g, '""')}"`;
}

/** Unformatted number so spreadsheets can compute on it; empty cell for no-data. */
function numericCell(value: string | number | null | undefined): string {
  return typeof value === "number" ? String(value) : "";
}

function percentCell(raw: string | number | null | undefined, domain: [number, number]): string {
  const percent = domainPercent(typeof raw === "number" ? raw : null, domain);
  if (percent === null) return "";
  const factor = 10 ** PERCENT_DECIMALS;
  return String(Math.round(percent * factor) / factor);
}

function listCell(values: readonly string[] | undefined, separator = LIST_SEPARATOR): string {
  return (values ?? []).join(separator);
}

function attributeCells(row: OverviewChartRow | undefined, hasGroupBy: boolean): string[] {
  return [
    listCell(row?.scenarios),
    listCell(row?.subFeatures),
    listCell(row?.sections, SECTION_SEPARATOR),
    listCell(row?.formats),
    ...(hasGroupBy ? [listCell(row?.groupings)] : []),
  ];
}

/**
 * Serialize the charted rows — top-N concepts × selected metrics — as CSV.
 * `rowsByTopic` is keyed by raw Topic_Title and supplies the Scenario / Sub-category / Section /
 * Format cells; a topic or field missing from it exports an empty cell. `groupByLabel` adds a
 * trailing attribute column named after the active Group By dimension, and is omitted when none is.
 *
 * Each metric contributes two columns: its raw KPI value and where that value sits
 * within the metric's own axis range — i.e. the bar height the chart draws. Raw values
 * alone would drop the scaling the chart is read through; percents alone would drop the
 * underlying KPI, which is not recoverable from a percent (every metric has its own range).
 */
export function buildChartCsv(
  data: ChartDatum[],
  metrics: ResolvedMetric[],
  rowsByTopic: ReadonlyMap<string, OverviewChartRow>,
  groupByLabel?: string,
): string {
  const header = [
    CONCEPT_COLUMN,
    SCENARIO_COLUMN,
    SUB_CATEGORY_COLUMN,
    SECTION_COLUMN,
    FORMAT_COLUMN,
    ...(groupByLabel ? [groupByLabel] : []),
    ...metrics.flatMap((m) => [m.label, `${m.label} ${PERCENT_COLUMN_SUFFIX}`]),
  ];

  const domains = computeMetricDomains(data, metrics);
  const rows = data.map((datum) => [
    formatTopicTitle(datum.category),
    ...attributeCells(rowsByTopic.get(datum.category), groupByLabel !== undefined),
    ...metrics.flatMap((m) => [
      numericCell(datum[rawMetricKey(m.id)]),
      percentCell(datum[rawMetricKey(m.id)], domains[m.id]!),
    ]),
  ]);

  return [header, ...rows].map((row) => row.map(escapeCsvField).join(",")).join("\n");
}

/** `overview-chart-2026-08-02.csv` — dated so repeat exports don't overwrite each other. */
export function chartCsvFilename(now: Date = new Date()): string {
  return `overview-chart-${now.toISOString().slice(0, 10)}.csv`;
}
